/* eslint-disable arrow-body-style */
import React, { useCallback, useMemo, useRef } from 'react';
import Filter from '@/pages/dashboard/components/NewFilter';
import { FORM_MAP } from '@/pages/dashboard/pages/overview/_config/constant';
import { setBtnPointLog } from "@/pages/dashboard/utils/utils";
import { useFilterData } from './useFunnelDailyFilter';
import { filterValueType, InputItemType } from '../../components/NewFilter/_type';
import { getLocale, shallowEqual, useIntl, useSelector } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getFilterSelector } from '../../components/NewFilter/util';
import { getLanguage } from '@home/utils/common';
import { renderStep } from './FilterList';

// 筛选器兜底路径
const FILTER_PATH = 'funnel/registerFunnelDaily';

/**
 *
 * @param props
 * @param props:filterConfirm 筛选器确认函数
 * @param props:filterReset 筛选器重置函数
 * @param props:filterInit 初始化函数，会向外部传递初始化后的筛选器值
 * @returns
 */

const RegisterFunnelDailyFilter = (props: any) => {
  const { filterConfirm, filterReset, filterInit, buttonLogConfig, openSticky = false, path = FILTER_PATH, children, isHaveFilterSave } = props;
  const filterValueRef = useRef<any>(null); // 筛选器值，此处不用state，更新formValue同样会触发组件更新
  const { formatMessage } = useIntl();
  const isEn = getLocale() === LANGUAGE_EN;
  let realPath = path;
  if (!realPath.startsWith("/")) {
    realPath = `/${realPath}`;
  }

  const gameConfig = useSelector((state: any) => {
    return state?.dashboardSetting?.gameConfig || {};
  }, shallowEqual);

  const { loading, selector } = useFilterData({ path });
  const { stage: stageList = [], attachmentData, latestDate } = selector || {};

  const { defaultStage, defaultIconStage } = useMemo(() => {
    const defaultStage = attachmentData?.default_stage_list?.map((item: any) => item?.code) || [];
    const defaultIconStage = attachmentData?.icon_stage_list?.map((item: any) => item?.code) || [];
    return {
      defaultStage,
      defaultIconStage
    };
  }, [attachmentData]);

  const defaultSelectArr = gameConfig?.page_selector?.find((item: any) => item?.path === realPath);
  let defaultSelect = defaultSelectArr?.filters;
  defaultSelect = getFilterSelector(gameConfig?.page_selector, defaultSelectArr, realPath?.trim()?.toLowerCase())?.filters;

  const inputList = useMemo(() => {
    return defaultSelect?.map((item: any) => {
      const name = getLanguage(isEn, item?.name_en?.toLowerCase(), item?.name_cn);
      if (item?.key === 'user_type') {
        // 当前后端不支持配置radioOptions，所以这里特殊处理，如果支持的话，inputlist可以直接传空到子组件
        return {
          ...item,
          tooltip: formatMessage({ id: 'funnel_filter_tips' }, { name}),
          radioOptions: [{
            value: 'All',
            text: formatMessage({ id: 'selector_multiple_component_custom_filter_all_describe' }),
            info: formatMessage({ id: 'selector_multiple_component_common_all_info' }, { name })
          }],
        };
      }

      if (item?.key === 'stage') {
        // 该自定义筛选器和之前页面的stage筛选器的key重复了，导致filter中映射的时候取的mapkey不对，
        // 这里做下兼容，设置optionKey，防止取数的时候映射SELECTOR_KEY_MAP后为空
        return {
          ...item,
          optionDataKey: 'stage',
          tooltip: formatMessage({ id: 'funnel_filter_tips' }, { name: getLanguage(isEn, name?.toLowerCase(), name)}),
          name,
          default: defaultStage,
          defaultIconStage,
          render: renderStep,
          isUseConfig: true,
        };
      }
      return {
        ...item,
        tooltip: formatMessage({ id: 'funnel_filter_tips' }, { name: getLanguage(isEn, name?.toLowerCase(), name)}),
        isUseConfig: true,
      };
    }) || [];
  }, [defaultSelect, isEn, defaultStage, defaultIconStage]);

  // 私有化处理，抹平新旧筛选器的差距
  const onInit = useCallback((value: filterValueType, inputList: InputItemType, isHaveExtendFIlter: boolean) => {
    filterValueRef.current = value;
    filterInit({
      ...(value || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: !isHaveExtendFIlter,
      stageList,
      defaultStage,
      defaultIconStage,
    });
  }, [filterInit, stageList, defaultStage, defaultIconStage]);

  const onConfirm = useCallback((value: filterValueType) => {
    filterValueRef.current = value;
    const obj: any = {
      ...(value || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: false,
      stageList,
      defaultStage,
      defaultIconStage,
    };
    filterConfirm(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value);
  }, [buttonLogConfig, filterConfirm, stageList, defaultStage, defaultIconStage]);

  const onReset = useCallback((value: filterValueType) => {
    filterValueRef.current = value;
    const obj = {
      ...(value || {}),
      [FORM_MAP.RANGE_TIME_DIFF]: true,
      stageList,
      defaultStage,
      defaultIconStage,
    };
    filterReset(obj);
    // 打点，沟通后打点操作放在业务代码的confirm和reset中，不封装在新版本筛选器中
    setBtnPointLog(buttonLogConfig, value, true);
  }, [buttonLogConfig, stageList, defaultStage, defaultIconStage, filterReset]);

  return <Filter
    loading={loading}
    outSelector={selector}
    openSticky={openSticky}
    outLatestDate={latestDate}
    path={path}
    value={filterValueRef.current}
    inputList={inputList}
    onInit={onInit}
    onConfirm={onConfirm}
    onReset={onReset}
    isHaveFilterSave={isHaveFilterSave}
  >
    {children}
  </Filter>;
};

export default RegisterFunnelDailyFilter;
